<template>
  <!--
    WHAT: Structured editor for the two supersession keys on opportunity_config.
          They are DIFFERENT MECHANISMS, not two views of one relationship, which
          is why this widget states each one's effect in the UI rather than just
          listing slugs:

          superseded_by  — DEFENSIVE, read on every sync (sync_opportunities).
            "Do not create an opportunity here if the patient already has a
            non-expired one in any of these programs." The TOC family all list
            each other, so the first discharge wins and the others stand down.

          supersedes     — ACTIVE, and read ONLY when create_strategy is
            merge_cm_programs (update_opportunities.merge_cm_programs). Creating
            this opportunity EXPIRES the patient's opportunities in the listed
            programs, and their active tasks, then creates a composite carrying
            merged metadata. Destructive by design.

    WHERE: rendered inside OpportunityConfigPanel's "Opportunity Sync" card.
    CONTRACT:
      props:  supersededBy (Array of slug), supersedes (Array of slug),
              createStrategy (String), metrics (Array [{id,name,metric_name_slug}]),
              selfSlug (String) — this metric's own slug, excluded from pickers
      emits:  update:supersededBy, update:supersedes
    NOTES:
      - Slugs, not ids: both readers resolve by metric_name_slug.
      - A metric with a NULL slug cannot be referenced (metric 2007 has one), so
        such metrics are filtered out of the pickers — selecting them would write
        a null that silently matches nothing.
      - supersedes is shown READ-ONLY unless create_strategy is merge_cm_programs,
        because it would otherwise be inert config that reads as active.
  -->
  <div>
    <label class="block text-[11px] font-medium text-gray-500 mb-1">Supersession</label>

    <!-- superseded_by ---------------------------------------------------- -->
    <div class="mb-2.5">
      <div class="flex items-center justify-between mb-1">
        <span class="text-[10px] text-gray-500">
          Stand down if the patient is already in
        </span>
        <button class="text-[10px] text-blue-600 hover:text-blue-700" @click="addBy">＋ add</button>
      </div>
      <p v-if="!byList.length" class="text-[10px] text-gray-400 italic">
        Nothing — this program always creates, regardless of other programs.
      </p>
      <div v-for="(slug, i) in byList" :key="'by' + i" class="flex items-center gap-2 mb-1">
        <select
          :value="slug"
          class="text-xs px-2 py-1 border border-gray-200 rounded flex-1 text-gray-800 focus:outline-none focus:border-blue-400"
          @change="setBy(i, $event.target.value)"
        >
          <option value="">— select program —</option>
          <option v-for="m in pickable" :key="m.metric_name_slug" :value="m.metric_name_slug">
            {{ m.name }} ({{ m.metric_name_slug }})
          </option>
          <option v-if="slug && !pickable.some((m) => m.metric_name_slug === slug)" :value="slug">
            {{ slug }} (not found)
          </option>
        </select>
        <button class="text-gray-400 hover:text-red-500 text-sm" @click="removeBy(i)">&times;</button>
      </div>
      <p v-if="byList.length" class="text-[10px] text-gray-400">
        Checked every sync against non-expired opportunities — a completed one still blocks
        at the same discharge.
      </p>
    </div>

    <!-- supersedes ------------------------------------------------------- -->
    <div class="border-t border-gray-100 pt-2.5">
      <div class="flex items-center justify-between mb-1">
        <span class="text-[10px] text-gray-500">
          Expire and absorb the patient's opportunities in
        </span>
        <button
          v-if="isMerge"
          class="text-[10px] text-blue-600 hover:text-blue-700"
          @click="addSup"
        >＋ add</button>
      </div>

      <p v-if="!isMerge" class="text-[10px] text-amber-600">
        Only applies when the create strategy is <span class="font-mono">merge_cm_programs</span>
        (currently <span class="font-mono">{{ createStrategy || 'unset' }}</span>).
        <template v-if="supList.length">
          The {{ supList.length }} entr{{ supList.length === 1 ? 'y' : 'ies' }} below
          {{ supList.length === 1 ? 'is' : 'are' }} stored but inert.
        </template>
      </p>

      <p v-else-if="!supList.length" class="text-[10px] text-red-500">
        Required by <span class="font-mono">merge_cm_programs</span> — with none listed the
        merge does nothing and logs an error.
      </p>

      <div v-for="(slug, i) in supList" :key="'sup' + i" class="flex items-center gap-2 mb-1">
        <select
          :value="slug"
          :disabled="!isMerge"
          class="text-xs px-2 py-1 border border-gray-200 rounded flex-1 text-gray-800 focus:outline-none focus:border-blue-400 disabled:bg-gray-50 disabled:text-gray-400"
          @change="setSup(i, $event.target.value)"
        >
          <option value="">— select program —</option>
          <option v-for="m in pickable" :key="m.metric_name_slug" :value="m.metric_name_slug">
            {{ m.name }} ({{ m.metric_name_slug }})
          </option>
          <option v-if="slug && !pickable.some((m) => m.metric_name_slug === slug)" :value="slug">
            {{ slug }} (not found)
          </option>
        </select>
        <button
          v-if="isMerge"
          class="text-gray-400 hover:text-red-500 text-sm"
          @click="removeSup(i)"
        >&times;</button>
      </div>

      <p v-if="isMerge && supList.length" class="text-[10px] text-amber-600">
        Destructive: creating this opportunity expires those opportunities and their
        active tasks. Logged work is preserved.
      </p>
    </div>

    <p v-if="overlap.length" class="text-[10px] text-red-500 mt-1.5">
      {{ overlap.join(', ') }} listed in both — a program cannot both block this one
      and be absorbed by it.
    </p>
  </div>
</template>

<script>
export default {
  name: 'SupersessionEditor',
  props: {
    supersededBy: { type: Array, default: () => [] },
    supersedes: { type: Array, default: () => [] },
    createStrategy: { type: String, default: '' },
    metrics: { type: Array, default: () => [] },
    selfSlug: { type: String, default: '' },
  },
  emits: ['update:supersededBy', 'update:supersedes'],
  data() {
    // LOCAL row state. A freshly-added row is an empty string, and the emitted
    // value is filtered to non-empty slugs — so the two cannot be the same list,
    // or "add" would strip its own new row before it could be filled.
    return {
      byList: [...(this.supersededBy || [])],
      supList: [...(this.supersedes || [])],
    };
  },
  watch: {
    supersededBy(v) {
      if (JSON.stringify((v || [])) === JSON.stringify(this.byList.filter(Boolean))) return;
      this.byList = [...(v || [])];
    },
    supersedes(v) {
      if (JSON.stringify((v || [])) === JSON.stringify(this.supList.filter(Boolean))) return;
      this.supList = [...(v || [])];
    },
  },
  computed: {
    isMerge() { return this.createStrategy === 'merge_cm_programs'; },
    // Only metrics that CAN be referenced: a slug is the join key, so a metric
    // without one is unusable here.
    pickable() {
      return (this.metrics || [])
        .filter((m) => m.metric_name_slug && m.metric_name_slug !== this.selfSlug)
        .slice()
        .sort((a, b) => (a.metric_name_slug > b.metric_name_slug ? 1 : -1));
    },
    overlap() {
      const s = new Set(this.supList.filter(Boolean));
      return this.byList.filter((x) => x && s.has(x));
    },
  },
  methods: {
    // Emit only non-empty slugs; a blank picker row must not persist a "" that
    // matches no metric. The row stays in local state until filled or removed.
    emitBy() { this.$emit('update:supersededBy', this.byList.filter(Boolean)); },
    emitSup() { this.$emit('update:supersedes', this.supList.filter(Boolean)); },
    addBy() { this.byList.push(''); },
    setBy(i, v) { this.byList[i] = v; this.emitBy(); },
    removeBy(i) { this.byList.splice(i, 1); this.emitBy(); },
    addSup() { this.supList.push(''); },
    setSup(i, v) { this.supList[i] = v; this.emitSup(); },
    removeSup(i) { this.supList.splice(i, 1); this.emitSup(); },
  },
};
</script>
